import { applyDatabinding } from '@dynamic/ui';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { MenuItem } from '#/client';
import { NavigationType } from '#/service/constants';

import { useConfig } from './useConfig';

export interface SeoInfo {
  title?: string;
}

const originalTitle = document.title;

/**
 * Sets the page title based on the current route and given params object.
 * When the given params contain empty properties then we will try to
 * extract meaningful information from the route and the menu's configuration
 *
 * @returns page title string that can be used as <h1> page title.
 */
export const useSeoInfo = (params: SeoInfo) => {
  const location = useLocation();
  const {
    menu: { mainMenuItems },
    runtime: { seo },
  } = useConfig();

  useEffect(() => {
    document.title =
      getTitle(params, mainMenuItems, seo?.title ?? '', location.pathname) ||
      seo?.defaultTitle ||
      originalTitle;
  }, [mainMenuItems, params, location, seo]);

  return (
    params.title || getCurrentMenuTitle(mainMenuItems, location.pathname, false)
  );
};

function getTitle(
  params: SeoInfo,
  mainMenuItems: MenuItem[],
  titleTemplate: string,
  pathname: string
) {
  const resParams = { ...params };

  if (!params.title) {
    resParams.title = getCurrentMenuTitle(mainMenuItems, pathname);
  }

  if (resParams.title) {
    return applyDatabinding({
      value: titleTemplate,
      context: resParams,
    });
  }

  return null;
}

/**
 * Gets name of the currently active page from the menu, so it's available for SEO purposes
 *
 * @param menu Menu from the configuration
 * @param pathname Current pathname from the `location`
 * @param ignoreHome @default true - whether to ignore home page
 * @returns title of the currently active page
 */
function getCurrentMenuTitle(
  menu: MenuItem[],
  pathname: string,
  ignoreHome = true
) {
  if (!menu || !pathname) {
    return undefined;
  }

  let normalizedPathname = pathname.toLowerCase();

  if (!ignoreHome && menu.length > 0 && pathname === '/') {
    const dest = menu[0].action.params?.destination;
    normalizedPathname = `/${dest?.type}/${dest?.id}`.toLowerCase();
  }

  const currentMenu = menu.find((e) => {
    const dest = e.action.params?.destination;
    const menuPath =
      dest?.type === NavigationType.Page
        ? `/${dest.type}/${dest.id}`
        : `/${dest?.id}`;

    return menuPath.toLowerCase() === normalizedPathname;
  });

  return currentMenu?.display.title;
}
